import { Box, Button, Heading, Image, Spinner, Text, VStack } from "@chakra-ui/react"
import { useEffect, useRef, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { verifyMagicLink } from "../../services/authApi"
import { markMagicLinkSignIn } from "../../utils/magicLinkSession"

const MagicLinkVerify: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const { login: authLogin } = useAuth()
  const navigate = useNavigate()
  const [authKey, setAuthKey] = useState<string | null>(null)
  const [isError, setIsError] = useState(false)
  const attempted = useRef(false)

  // Step 1: fire the API call once
  useEffect(() => {
    if (!token || attempted.current) return
    attempted.current = true

    verifyMagicLink(token)
      .then((response) => {
        const key = response.data?.key
        if (key) {
          setAuthKey(key)
        } else {
          setIsError(true)
        }
      })
      .catch(() => setIsError(true))
  }, [token])

  // Step 2: once the key lands in state, log in and navigate inside a React effect
  useEffect(() => {
    if (!authKey) return
    markMagicLinkSignIn()
    authLogin(authKey)
    navigate("/dashboard", { replace: true })
  }, [authKey])

  const isLoading = !authKey && !isError

  return (
    <>
      <Box pb={8}>
        <Link to="/">
          <Image w={{ base: 150, md: 200 }} src="/greenflip_logo.png" alt="GreenFlip" />
        </Link>
      </Box>

      <Box
        bg="gray.100"
        px={{ base: 6, md: 8, lg: 12 }}
        py={{ base: 6, md: 8, lg: 10 }}
        borderRadius="3xl"
        w={{ base: "95%", md: "85%", lg: "80%" }}
        maxW="500px"
        textAlign="center"
      >
        {isLoading && (
          <VStack gap={4}>
            <Spinner size="lg" color="green.800" />
            <Heading size="md">Signing you in…</Heading>
          </VStack>
        )}

        {isError && (
          <VStack gap={4}>
            <Heading size="md">Link expired or already used</Heading>
            <Text fontSize="sm" color="gray.600">
              Sign-in links are single-use.
            </Text>
            <Button
              as={Link}
              to="/login"
              variant="outline"
              size="sm"
              borderColor="gray.300"
            >
              Request a new link
            </Button>
          </VStack>
        )}
      </Box>
    </>
  )
}

export default MagicLinkVerify
